import React from 'react';

function SecuritiesTradingBrokerage() {
  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[420px] overflow-hidden">
        <video
          src="/images/global-market.mp4"
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black/80" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-white text-4xl sm:text-5xl md:text-6xl font-light tracking-[-0.02em]">Securities Trading & Brokerage</h1>
            <p className="mt-4 text-white/90 max-w-2xl text-lg">Institutional execution quality, smart order routing, and rigorous risk controls across global markets.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="/apply" className="px-6 py-3 rounded-md bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] text-black font-semibold">Apply now</a>
              <a href="/contact" className="px-6 py-3 rounded-md border border-white/30 text-white">Contact us</a>
            </div>
          </div>
        </div>
      </section>

      {/* Capability + visual */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-semibold text-gray-900">Execution, Liquidity, Transparency</h2>
            <p className="mt-4 text-gray-600">DMA and algorithmic strategies including VWAP, TWAP, POV and Implementation Shortfall. Our router seeks price improvement while respecting your urgency and risk constraints.</p>
            <ul className="mt-6 space-y-3 text-gray-700">
              <li className="flex items-start gap-3"><span className="w-2 h-2 mt-2 rounded-full bg-[#0056d7]" />Cross‑venue smart order routing</li>
              <li className="flex items-start gap-3"><span className="w-2 h-2 mt-2 rounded-full bg-[#0056d7]" />Pre‑/post‑trade analytics and TCA</li>
              <li className="flex items-start gap-3"><span className="w-2 h-2 mt-2 rounded-full bg-[#0056d7]" />Kill‑switches and circuit breakers</li>
            </ul>
          </div>
          <div className="relative">
            <img src="/images/traders-profit-279315.jpg" alt="Execution" className="w-full h-96 object-cover rounded-2xl shadow-xl" />
            <div className="absolute -bottom-5 -left-5 w-40 h-40 bg-[#0056d7] opacity-10 rounded-3xl" />
          </div>
        </div>
      </section>

      {/* Feature Cards */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-6">
            {[
              { t: 'Risk Controls', d: 'Pre-trade checks, hard limits, and real-time monitoring.', img: '/images/man-is-holding-tablet-with-graph-him_479259-14595.jpg_dz4zsm.jpg' },
              { t: 'Connectivity', d: 'Web, API and FIX access; live balances and margin.', img: '/images/business-person-futuristic-business-environment.jpg' },
              { t: 'Reporting', d: 'Fills, P&L, and compliance-ready exports with TCA.', img: '/images/tablet-business-overlay.jpg' }
            ].map((x) => (
            <div key={x.t} className="rounded-2xl overflow-hidden bg-white shadow border border-gray-100">
              <img src={x.img} alt={x.t} className="w-full h-36 object-cover" />
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900">{x.t}</h3>
                <p className="mt-2 text-gray-600 text-sm">{x.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Platforms showcase */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-gray-900">Platforms</h2>
            <p className="mt-2 text-gray-600">Multi‑channel access built for speed and control.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 items-stretch">
            {[
              { t:'Web Trader', d:'Intuitive UI, watchlists, and live order management.', img:'/images/two-stock-exchange-traders-discussing-dynamic-investment-graph-sellable_31965-313820.jpg_yeupoj.jpg' },
              { t:'API & FIX', d:'Programmatic trading with robust docs and sandbox.', img:'/images/business-people-working-as-team-conference-room-plans-future_482257-25781.jpg_nur6h3.jpg' },
              { t:'Mobile', d:'Trade and monitor on‑the‑go with secure biometrics.', img:'/images/medium-shot-people-sitting-together_23-2149271699.jpg_arrchf.jpg' }
            ].map((p)=> (
              <div key={p.t} className="rounded-2xl overflow-hidden border border-gray-100 shadow-sm bg-white flex flex-col">
                <img src={p.img} alt={p.t} className="w-full h-44 object-cover" />
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-lg font-semibold text-gray-900">{p.t}</h3>
                  <p className="mt-2 text-gray-600 text-sm">{p.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process steps */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-6">
            {[{s:'1',t:'Onboarding',d:'KYC, account setup and platform access.'},{s:'2',t:'Funding',d:'Transfer assets or deposit to begin trading.'},{s:'3',t:'Execution',d:'Route via algos or direct DMA with risk controls.'},{s:'4',t:'Reporting',d:'Daily confirms, P&L and compliance exports.'}].map(step => (
              <div key={step.s} className="relative p-6 bg-white/5 border border-white/10 rounded-xl">
                <div className="absolute -top-3 left-6 w-8 h-8 rounded-full bg-white text-gray-900 flex items-center justify-center text-xs font-bold">{step.s}</div>
                <h3 className="mt-2 text-lg font-semibold">{step.t}</h3>
                <p className="mt-1 text-white/80 text-sm">{step.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <a href="/apply" className="inline-block px-8 py-3 rounded-md bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] text-black font-semibold">Open account</a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default SecuritiesTradingBrokerage;
